import {
  useEffect,
  useRef,
  type FC,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { useEscapeToClose } from '~/hooks/useEscapeToClose';
import { ThemedLayout } from '~/layout/ThemedLayout';

// The second click of the double click that opened the modal lands on the backdrop.
const BACKDROP_GRACE_MS = 300;

export interface ModalProps {
  isOpen: boolean;
  onClose?: () => void;
  onOutsideClick?: () => void;
  children: ReactNode;
  className?: string;
  zIndex?: number;
  canClose?: boolean;
}

export const Modal: FC<ModalProps> = ({
  isOpen,
  onClose,
  onOutsideClick,
  children,
  className = '',
  zIndex = 1000,
  canClose = true,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const openedAtRef = useRef(0);

  useEscapeToClose({ isOpen, onClose, canClose });

  useEffect(() => {
    if (isOpen) {
      openedAtRef.current = Date.now();
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleBackdropClick = (event: MouseEvent<HTMLDivElement>) => {
    if (
      canClose &&
      event.target === event.currentTarget &&
      Date.now() - openedAtRef.current >= BACKDROP_GRACE_MS
    ) {
      if (onOutsideClick) {
        onOutsideClick();
      } else if (onClose) {
        onClose();
      }
    }
  };

  useEffect(() => {
    if (isOpen && modalRef.current) {
      const focusableElements = modalRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const firstElement = focusableElements[0] as HTMLElement;

      if (firstElement) {
        firstElement.focus();
      }
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const ModalContent = (
    <ThemedLayout>
      <div
        className="fixed inset-0 flex items-center justify-center p-4"
        style={{
          zIndex,
          backgroundColor: 'rgba(0, 0, 0, 0.55)',
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)',
        }}
        onClick={handleBackdropClick}
        role="Modal"
        aria-modal="true"
        aria-labelledby="Modal-title"
      >
        <div
          ref={modalRef}
          className={`
          relative
          shadow-2xl
          max-w-full
          max-h-full
          overflow-auto
          transform
          transition-all
          duration-500
          ease-out
          animate-in
          fade-in-0
          zoom-in-95
          ${className}
        `}
          onClick={(e) => e.stopPropagation()}
        >
          {children}
        </div>
      </div>
    </ThemedLayout>
  );

  return createPortal(ModalContent, document.body);
};
